export type ProposalItemShape={
  proposed_values?:Record<string,unknown>|undefined;
  draft_values?:{editorState?:Record<string,string>}|undefined;
  draft_version?:number|undefined;
  property_link_valid?:boolean|undefined;
  repository_category_code?:string|undefined;
  repository_subcategory_code?:string|undefined;
  repository_document_type_code?:string|undefined;
  repository_document_type_name?:string|undefined;
};

const text=(value:unknown)=>value===null||value===undefined?'':String(value);

export function proposalEditorState(item:ProposalItemShape,defaultDevelopmentId=''){
  const proposed=item.proposed_values??{};
  const links=proposed.proposedLinks&&typeof proposed.proposedLinks==='object'
    ?proposed.proposedLinks as Record<string,unknown>:{};
  const link=(prefix:string)=>Object.keys(links).find(key=>key.startsWith(prefix));
  const developmentKey=link('Development:');
  const locationKey=link('Location:');
  const assetKey=link('Asset:');
  const contactKey=link('Contact:');
  const contactDetail=contactKey&&links[contactKey]&&typeof links[contactKey]==='object'
    ?links[contactKey] as Record<string,unknown>:{};
  const canonicalPrimary=text(proposed.taxonomyPrimaryCode||item.repository_category_code);
  const canonicalSecondary=text(proposed.taxonomySecondaryCode||item.repository_subcategory_code);
  const legacyEncoded=text(item.repository_document_type_code);
  const [legacyTertiary='',legacyKind='']=legacyEncoded.includes('::')?legacyEncoded.split('::',2):['',''];
  const canonicalTertiary=text(proposed.taxonomyTertiaryCode||legacyTertiary);
  const canonicalKind=text(proposed.documentKindCode||legacyKind);
  const serverDraft=item.draft_version&&item.draft_values?.editorState?item.draft_values.editorState:{};
  const state:Record<string,string>={
    associationScope:text(proposed.associationScope)|| (locationKey?(Object.keys(links).filter(key=>key.startsWith('Location:')).length>1?'Several units':'Unit-specific document'):developmentKey||defaultDevelopmentId?'Building-level document':'Portfolio-level document'),
    propertyNodeIds:JSON.stringify(item.property_link_valid===false?[]:Object.keys(links).filter(key=>key.startsWith('Location:')).map(key=>key.slice(9))),
    relatedParties:JSON.stringify(Object.entries(links).filter(([key])=>key.startsWith('Contact:')).map(([key,detail])=>({id:key.slice(8),type:text((detail as any)?.partyType),name:text((detail as any)?.name),purpose:text((detail as any)?.relationshipPurpose)}))),
    additionalDates:JSON.stringify(proposed.additionalDates??[]),
    scope:text(proposed.scope),relevantDateType:text(proposed.relevantDateType),relevantDate:text(proposed.relevantDate),expiryDate:text(proposed.expiryDate),
    reference:text(proposed.reference),revision:text(proposed.revision),sensitivity:text(proposed.sensitivity),
    disposition:text(proposed.disposition),accessReason:text(proposed.accessReason),issuer:text(proposed.issuer),
    issuerPartyId:text(proposed.issuerPartyId),issuerPartyType:text(proposed.issuerPartyType),issuerRole:text(proposed.issuerRole||'Issuer'),
    taxonomyPrimaryCode:canonicalPrimary,taxonomySecondaryCode:canonicalSecondary,taxonomyTertiaryCode:canonicalTertiary,
    documentKindCode:canonicalKind,repositoryCategoryCode:canonicalPrimary,repositorySubcategoryCode:canonicalSecondary,
    repositoryDocumentTypeCode:canonicalTertiary||canonicalKind?`${canonicalTertiary}::${canonicalKind}`:'',
    documentType:text(proposed.documentType||item.repository_document_type_name),
    developmentId:developmentKey?.slice(12)??defaultDevelopmentId,
    propertyNodeId:item.property_link_valid===false?'':locationKey?.slice(9)??'',assetId:assetKey?.slice(6)??'',
    relatedPartyId:contactKey?.slice(8)??'',relatedPartyType:text(contactDetail.partyType),
    relatedPartyName:text(contactDetail.name),relatedPartyPurpose:text(contactDetail.relationshipPurpose),
    ...serverDraft,
  };
  // Recover selections autosaved by releases which displayed repository aliases while Save read
  // canonical fields. This is the user's latest recoverable input, not a new classification.
  const draftEncoded=text(serverDraft.repositoryDocumentTypeCode);
  const [draftTertiary='',draftKind='']=draftEncoded.includes('::')?draftEncoded.split('::',2):['',''];
  if(!state.taxonomyPrimaryCode)state.taxonomyPrimaryCode=text(serverDraft.repositoryCategoryCode)||canonicalPrimary;
  if(!state.taxonomySecondaryCode)state.taxonomySecondaryCode=text(serverDraft.repositorySubcategoryCode)||canonicalSecondary;
  if((!Object.hasOwn(serverDraft,'taxonomyTertiaryCode')||['taxonomyPrimaryCode','taxonomySecondaryCode','documentKindCode'].every(key=>serverDraft[key]===''))&&!state.taxonomyTertiaryCode)state.taxonomyTertiaryCode=draftTertiary||canonicalTertiary;
  if(!state.documentKindCode)state.documentKindCode=draftKind||canonicalKind;
  // Older drafts created by the broken editor contain empty taxonomy aliases. They must not erase
  // the newer canonical proposal saved by Create review proposal.
  state.repositoryCategoryCode=state.taxonomyPrimaryCode;
  state.repositorySubcategoryCode=state.taxonomySecondaryCode;
  state.repositoryDocumentTypeCode=state.taxonomyTertiaryCode||state.documentKindCode
    ?`${state.taxonomyTertiaryCode}::${state.documentKindCode}`:'';
  return state;
}

export function proposalRoundTrips(submitted:Record<string,unknown>,returned:Record<string,unknown>){
  return ['taxonomyPrimaryCode','taxonomySecondaryCode','taxonomyTertiaryCode','documentKindCode','scope']
    .every(field=>text(returned[field])===text(submitted[field]));
}

export type CanonicalClassificationErrors=Partial<Record<'taxonomyPrimaryCode'|'taxonomySecondaryCode'|'taxonomyTertiaryCode'|'documentKindCode',string>>;
export function validateEditorClassification(values:Record<string,string>):CanonicalClassificationErrors{
  return {
    ...(!values.taxonomyPrimaryCode?{taxonomyPrimaryCode:'Choose a primary category'}:{}),
    ...(!values.taxonomySecondaryCode?{taxonomySecondaryCode:'Choose a secondary category'}:{}),
    ...(!values.documentKindCode?{documentKindCode:'Choose a document type'}:{}),
  };
}
